import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListFormPageHeader from '../../components/ListFormPageHeader';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { printDieselConsumption } from '../../utils/dieselConsumptionPrint';
import {
    deleteDieselConsumption,
    getDieselConsumptionList,
} from '../../services/DieselConsumptionServices';
import { buildDieselConsumptionQueryParams } from '../../utils/listViewDateRanges';
import { getDefaultListViewConfig } from '../../config/listViewSettingsConfig';
import { bootstrapListViewLoad } from '../../utils/listViewBootstrap';
import ListPageGradientSummary from '../../components/ListPageGradientSummary';
import { buildGradientCard, formatQty, formatRecordStatus, splitRecordsByStatus, sumField, buildCancelledSummaryCard } from '../../utils/listSummaryUtils';
import moment from 'moment';

function DieselConsumptionTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const todayDateLabel = moment().format('DD-MM-YYYY');
    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayCount = activeRecords.filter((row) => row.consumption_date === todayDateLabel).length;
        return buildGradientCard('Today Diesel', todayCount, 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total Notes', activeRecords.length, 'blue'),
            buildGradientCard('Diesel Qty', formatQty(sumField(activeRecords, 'total_diesel_qty')), 'purple'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            valueField: 'total_diesel_qty',
            entityLabel: 'note cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Diesel Notes (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled diesel notes in the current list.',
        columns: [
            { key: 'consumption_no_display', label: 'Consumption No', align: 'center' },
            { key: 'consumption_date', label: 'Date', align: 'center' },
            { key: 'plant_alias', label: 'Plant', align: 'left' },
            { key: 'equipment_summary', label: 'Equipment', align: 'left' },
            { key: 'total_diesel_qty', label: 'Diesel Qty', align: 'right' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            consumption_no_display: row.consumption_no_display,
            consumption_date: row.consumption_date,
            plant_alias: row.plant_alias,
            equipment_summary: row.equipment_summary,
            total_diesel_qty: formatQty(row.total_diesel_qty),
        }),
        footerRowBuilder: (rows) => ({
            consumption_no_display: 'Total',
            consumption_date: '',
            plant_alias: '',
            equipment_summary: `${rows.length} Note`,
            total_diesel_qty: formatQty(sumField(rows, 'total_diesel_qty')),
        }),
    }), [cancelledRecords]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'consumption_no_display', headerName: 'Consumption No' },
        { fieldName: 'consumption_date', headerName: 'Date' },
        { fieldName: 'equipment_summary', headerName: 'Equipment' },
        { fieldName: 'total_diesel_qty', headerName: 'Diesel Qty' },
        { fieldName: 'issued_by', headerName: 'Issued By' },
        { fieldName: 'status', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const loadDieselList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('diesel_consumption');
        setIsLoading(true);
        return getDieselConsumptionList(cookies, {
            ...buildDieselConsumptionQueryParams(activeConfig),
            limit: 500,
        })
            .then((response) => {
                const records = (response.diesel_consumption_list || []).map((row) => {
                    const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                    return {
                        ...row,
                        consumption_no_display: row.consumption_no,
                        status: formatRecordStatus(isActive),
                        hide_row_edit: 'No',
                        hide_row_delete: isActive ? 'Yes' : 'No',
                        hide_row_print: 'No',
                    };
                });
                setData({ records });
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Failed to load diesel consumption notes');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadDieselList(config);
    }, [loadDieselList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'diesel_consumption',
            loadFn: loadDieselList,
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadDieselList]);

    const handleOpen = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status === 'Active';
        navigate(isActive ? `/DieselConsumptionEdit/${id}` : `/DieselConsumptionView/${id}`);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Record deleted.', 'success');
                    loadDieselList();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'));
        });
    };

    const handlePrint = (id) => {
        printDieselConsumption(cookies, id).catch((error) => {
            displayError(error?.response?.data || error, 'Print Failed');
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((row) => ({
                plant: row.plant_alias,
                consumption_no: row.consumption_no_display,
                date: row.consumption_date,
                equipment: row.equipment_summary,
                diesel_qty: row.total_diesel_qty,
                issued_by: row.issued_by,
                status: row.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'Diesel Consumption Notes',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'DieselConsumption.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="STORE"
                    title="List of Diesel Consumption Notes"
                    viewKey="diesel_consumption"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/DieselConsumption"
                    addNewLabel="New Diesel Consumption"
                />
                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="List of Diesel Consumption Notes"
                            data={data.records}
                            columns={columns}
                            isLoading={isLoading}
                            onRowEdit={handleOpen}
                            onRowDelete={handleDelete}
                            onRowPrint={handlePrint}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            hideDelete={false}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DieselConsumptionTable;
